import { useEffect, useState } from "react";

/**
 * FolelseskortCarousel — lightweight CSS-driven animated carousel of the
 * 8 hero emotion cards (drawn programmatically, no external assets).
 *
 * Cycles cards every 2s with a slide-in + fade transition. All images
 * are pre-generated PNGs served from /api/previews-inline/folelseskort-cards/
 * (small, cached by the CDN — total payload ~350 KB).
 *
 * Falls back gracefully if an image fails to load.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const HERO_CARDS = [
  { slug: "glad",   name: "GLAD",   accent: "#6FA585" },
  { slug: "trist",  name: "TRIST",  accent: "#5A8A9A" },
  { slug: "sint",   name: "SINT",   accent: "#E8A09A" },
  { slug: "redd",   name: "REDD",   accent: "#E8C97A" },
  { slug: "stolt",  name: "STOLT",  accent: "#5A8A9A" },
  { slug: "spent",  name: "SPENT",  accent: "#6FA585" },
  { slug: "trygg",  name: "TRYGG",  accent: "#E8A09A" },
  { slug: "modig",  name: "MODIG",  accent: "#E8C97A" },
];

const INTERVAL_MS = 2000;

export function FolelseskortCarousel({ title }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % HERO_CARDS.length);
    }, INTERVAL_MS);
    return () => clearInterval(t);
  }, [paused]);

  return (
    <div
      data-testid="folelseskort-carousel"
      className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-[#FAF6F1] border border-[#E5E2DC]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Frames */}
      {HERO_CARDS.map((card, i) => {
        const active = i === index;
        return (
          <img
            key={card.slug}
            src={`${BACKEND}/api/previews-inline/folelseskort-card-${card.slug}.png`}
            alt={`${title} – ${card.name}`}
            data-testid={`carousel-card-${card.slug}`}
            className={`absolute inset-0 w-full h-full object-contain p-6 transition-all duration-700 ease-out ${
              active
                ? "opacity-100 translate-x-0 scale-100"
                : "opacity-0 -translate-x-6 scale-95 pointer-events-none"
            }`}
            loading={i === 0 ? "eager" : "lazy"}
            onError={(e) => { e.currentTarget.style.opacity = 0; }}
          />
        );
      })}

      {/* Dots indicator */}
      <div
        className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5"
        data-testid="carousel-dots"
      >
        {HERO_CARDS.map((c, i) => (
          <button
            key={c.slug}
            type="button"
            aria-label={c.name}
            data-testid={`carousel-dot-${c.slug}`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === index
                ? "w-6 bg-[#2C302E]"
                : "w-1.5 bg-[#2C302E]/25 hover:bg-[#2C302E]/50"
            }`}
          />
        ))}
      </div>

      {/* Small caption */}
      <div
        className="absolute top-4 right-4 bg-white/80 backdrop-blur-sm rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-[#2C302E]"
        data-testid="carousel-badge"
      >
        32 kort
      </div>
    </div>
  );
}
